import { computed, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { takeUntil, tap } from 'rxjs';
import { TenantContextService } from '../../services/tenant-context.service';

export interface CompanyProfile {
  name: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  readonly latitude?: number | null;
  readonly longitude?: number | null;
}

const initialProfile: Readonly<CompanyProfile> = Object.freeze({
  name: '', street: '', city: '', region: '', postalCode: '', country: '',
});

/** Company identity is owned by Account settings; this cache is cleared at the session boundary. */
@Injectable({ providedIn: 'root' })
export class CompanyProfileService {
  private readonly saved = signal(initialProfile);
  readonly profile = this.saved.asReadonly();
  readonly address = computed(() => {
    const p = this.profile();
    return [p.street, p.city, [p.region, p.postalCode].filter(Boolean).join(' '), p.country].filter(Boolean).join(', ');
  });

  constructor(private readonly http: HttpClient, private readonly tenant: TenantContextService) {
    tenant.sessionChanged$.pipe(takeUntilDestroyed()).subscribe(() => this.saved.set(initialProfile));
  }

  load() {
    return this.http.get<CompanyProfile>('/api/account-settings/company')
      .pipe(takeUntil(this.tenant.sessionChanged$), tap(profile => this.saved.set(profile)));
  }
  location() {
    return this.http.get<{latitude:number;longitude:number}>('/api/account-settings/company/location')
      .pipe(takeUntil(this.tenant.sessionChanged$));
  }
  save(profile: CompanyProfile) {
    return this.http.put<CompanyProfile>('/api/account-settings/company', profile)
      .pipe(takeUntil(this.tenant.sessionChanged$), tap(saved => this.saved.set(saved)));
  }
}
